import type { ReactNode } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

/**
 * The check-in screens' grouped card (approved "Check in" frames, #2691): a
 * short bold header row and divided rows, each with a leading icon, a title,
 * one quiet detail line, optional extra content and one trailing action.
 */
export function CheckInCard({
  title,
  titleId,
  children,
}: {
  title: string;
  titleId?: string;
  children: ReactNode;
}) {
  return (
    <Paper
      component="section"
      variant="outlined"
      aria-labelledby={titleId}
      sx={{ borderRadius: "10px", overflow: "hidden", flex: "none" }}
    >
      <Typography
        id={titleId}
        component="h2"
        sx={{
          minHeight: 36,
          display: "flex",
          alignItems: "center",
          px: 1.5,
          fontSize: "0.8125rem",
          fontWeight: 700,
          color: "text.secondary",
        }}
      >
        {title}
      </Typography>
      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0 }}>
        {children}
      </Box>
    </Paper>
  );
}

export function CheckInRow({
  icon,
  title,
  detail,
  extra,
  trailing,
  alignTop = false,
}: {
  icon: ReactNode;
  title: ReactNode;
  detail?: ReactNode;
  extra?: ReactNode;
  trailing?: ReactNode;
  alignTop?: boolean;
}) {
  return (
    <Box
      component="li"
      sx={{
        display: "flex",
        alignItems: alignTop ? "flex-start" : "center",
        gap: 1.25,
        px: 1.5,
        py: 1.125,
        borderTop: 1,
        borderColor: "divider",
        fontSize: "0.8125rem",
      }}
    >
      <Box
        aria-hidden
        sx={{
          display: "flex",
          flex: "none",
          mt: alignTop ? 0.25 : 0,
          "& svg": { fontSize: 18 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          component="p"
          sx={{
            m: 0,
            fontSize: "0.8125rem",
            fontWeight: 600,
            lineHeight: 1.4,
            overflowWrap: "anywhere",
          }}
        >
          {title}
        </Typography>
        {detail ? (
          <Typography
            component="p"
            sx={{ m: 0, fontSize: "0.75rem", lineHeight: 1.4, color: "text.secondary" }}
          >
            {detail}
          </Typography>
        ) : null}
        {extra}
      </Box>
      {trailing ? <Box sx={{ flex: "none" }}>{trailing}</Box> : null}
    </Box>
  );
}

/** Visit dots: filled for a visit still left, outlined for a used one. */
export function VisitDots({ dots, label }: { dots: boolean[]; label: string }) {
  return (
    <Box
      role="img"
      aria-label={label}
      sx={{ display: "flex", gap: "4px", mt: 0.75, flexWrap: "wrap" }}
    >
      {dots.map((left, index) => (
        <Box
          key={index}
          component="span"
          sx={{
            width: 9,
            height: 9,
            borderRadius: "50%",
            boxSizing: "border-box",
            ...(left
              ? { bgcolor: "primary.main" }
              : { border: "1.5px solid", borderColor: "divider" }),
          }}
        />
      ))}
    </Box>
  );
}
